import {
  ADDRESSING_MODE_SEEDS,
  ASSEMBLER_GUIDE_SEEDS,
  BASIC_GUIDE_SEEDS,
  DEBUGGER_GUIDE_SEEDS,
  FORTH_GUIDE_SEEDS,
  TRACKER_GUIDE_SEEDS,
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  COMMON_LOOKUP_IDS,
  FLAG_SEEDS,
  HIGHLIGHTED_IDS,
  INTERRUPT_SEEDS,
  ISA_SPEC,
  MEMORY_RANGE_SEEDS,
  OPCODE_VARIANT_ROWS,
  PUBLIC_API_SEEDS,
  QUICK_ENTRIES,
  REGISTER_SEEDS,
  SHELL_COMMAND_SEEDS
} from "./data.js";
import type {
  AddressingModeEntry,
  AssemblerGuideEntry,
  BasicGuideEntry,
  DebuggerGuideEntry,
  FlagEntry,
  ForthGuideEntry,
  InterruptEntry,
  MemoryRangeEntry,
  OpcodeEntry,
  OpcodeVariant,
  PublicApiEntry,
  ReferenceCatalog,
  ReferenceEntry,
  ReferenceKind,
  RegisterEntry,
  ShellCommandEntry,
  TrackerGuideEntry
} from "./types.js";

const REFERENCE_HASH_PREFIX = "#reference/";

const FLAG_ID_BY_NAME: Record<string, string> = {
  C: "flag-c",
  I: "flag-i",
  N: "flag-n",
  V: "flag-v",
  Z: "flag-z"
};

const MODE_EXAMPLE_BY_ID: Record<string, string> = {
  abs: "$1234",
  absx: "$1234,X",
  absy: "$1234,Y",
  acc: "A",
  imm: "#$10",
  imp: "",
  ind: "($1234)",
  indx: "($20,X)",
  indx_post: "($20),X",
  indy: "($20),Y",
  indy_pre: "($20,Y)",
  rel: "LABEL",
  zp: "$20",
  zpx: "$20,X",
  zpy: "$20,Y"
};

const MODE_SAMPLE_ORDER = [
  "imp",
  "acc",
  "imm",
  "zp",
  "abs",
  "ind",
  "rel",
  "zpx",
  "zpy",
  "absx",
  "absy",
  "indx",
  "indy",
  "indy_pre",
  "indx_post"
];

type CanonicalOpcodeRow = {
  bytes: number;
  mnemonic: string;
  modeId: string;
  opcode: string;
};

type OpcodeSupplement = {
  cycles: string;
  flags: string[];
  flagsText: string;
  mnemonic: string;
  modeId: string;
};

function uniqueStrings(values: Iterable<string>): string[] {
  return Array.from(new Set(Array.from(values).filter((value) => value !== "")));
}

function modeIdToEntryId(modeId: string): string {
  return `addressing-mode-${modeId.replace(/_/g, "-")}`;
}

function parseFlags(flagsText: string): string[] {
  return uniqueStrings(flagsText.match(/\b[NVIZC]\b/g) ?? []);
}

function formatOpcodeByte(code: number): string {
  return `0x${code.toString(16).toUpperCase().padStart(2, "0")}`;
}

function buildCanonicalOpcodeRows(): CanonicalOpcodeRow[] {
  const rows: CanonicalOpcodeRow[] = [];
  for (const group of ISA_SPEC.groups) {
    for (const modeId of group.modes) {
      const mode = ISA_SPEC.modes[modeId];
      if (mode === undefined || mode.group_nibble === undefined) {
        throw new Error(`missing grouped mode metadata for ISA mode '${modeId}'`);
      }
      rows.push({
        bytes: mode.size,
        mnemonic: group.mnemonic,
        modeId,
        opcode: formatOpcodeByte(group.group_nibble * 0x10 + mode.group_nibble)
      });
    }
  }
  for (const special of ISA_SPEC.specials) {
    const mode = ISA_SPEC.modes[special.mode];
    if (mode === undefined) {
      throw new Error(`missing ISA mode '${special.mode}' for opcode ${special.mnemonic}`);
    }
    rows.push({
      bytes: mode.size,
      mnemonic: special.mnemonic,
      modeId: special.mode,
      opcode: formatOpcodeByte(special.code)
    });
  }
  return rows.sort((left, right) => Number.parseInt(left.opcode.slice(2), 16) - Number.parseInt(right.opcode.slice(2), 16));
}

function parseOpcodeSupplementByOpcode(): Map<string, OpcodeSupplement> {
  const supplements = new Map<string, OpcodeSupplement>();
  for (const row of OPCODE_VARIANT_ROWS.split("\n")) {
    const [opcode, mnemonic, modeId, cycles, flagsText] = row.split("|");
    if (supplements.has(opcode)) {
      throw new Error(`duplicate opcode supplement ${opcode}`);
    }
    supplements.set(opcode, {
      cycles,
      flags: parseFlags(flagsText),
      flagsText,
      mnemonic,
      modeId
    });
  }
  return supplements;
}

function buildExample(mnemonic: string, modeId: string): string {
  const operand = MODE_EXAMPLE_BY_ID[modeId] ?? "";
  return operand === "" ? mnemonic : `${mnemonic} ${operand}`;
}

function relatedRegistersForMnemonic(mnemonic: string): string[] {
  const aMnemonics = new Set([
    "ADC",
    "AND",
    "ASL",
    "ASR",
    "CMP",
    "EOR",
    "LDA",
    "LSR",
    "ORA",
    "PLA",
    "ROL",
    "ROR",
    "SBC",
    "STA",
    "TAX",
    "TAY",
    "TXA",
    "TYA"
  ]);
  const xMnemonics = new Set(["DEX", "INX", "LDX", "STX", "TAX", "TSX", "TXA", "TXS"]);
  const yMnemonics = new Set(["DEY", "INY", "LDY", "STY", "TAY", "TSY", "TYA", "TYS"]);
  const stackMnemonics = new Set(["BRK", "JSR", "PHA", "PHP", "PLA", "PLP", "RTI", "RTS", "TSX", "TSY", "TXS", "TYS"]);
  const pcMnemonics = new Set([
    "BCC",
    "BCS",
    "BEQ",
    "BMI",
    "BNE",
    "BPL",
    "BRK",
    "BVC",
    "BVS",
    "JCC",
    "JCS",
    "JEQ",
    "JMI",
    "JMP",
    "JNE",
    "JPL",
    "JSR",
    "JVC",
    "JVS",
    "RTI",
    "RTS"
  ]);
  const related: string[] = [];
  if (aMnemonics.has(mnemonic)) related.push("register-a");
  if (xMnemonics.has(mnemonic)) related.push("register-x");
  if (yMnemonics.has(mnemonic)) related.push("register-y");
  if (stackMnemonics.has(mnemonic)) related.push("register-sp");
  if (pcMnemonics.has(mnemonic)) related.push("register-pc");
  if (["BCC", "BCS", "BEQ", "BMI", "BNE", "BPL", "BVC", "BVS", "JCC", "JCS", "JEQ", "JMI", "JNE", "JPL", "JVC", "JVS", "PLP", "PHP", "RTI", "SEI", "CLI", "SEC", "CLC", "ADC", "SBC", "CMP"].includes(mnemonic)) {
    related.push("register-p");
  }
  return uniqueStrings(related);
}

function mnemonicDescription(mnemonic: string): { keywords: string[]; summary: string } {
  const loadMap: Record<string, string> = {
    LDA: "A",
    LDX: "X",
    LDY: "Y"
  };
  if (mnemonic in loadMap) {
    const target = loadMap[mnemonic];
    return {
      keywords: ["load", `register ${target.toLowerCase()}`],
      summary: `Loads memory or an immediate byte into ${target}.`
    };
  }

  const storeMap: Record<string, string> = {
    STA: "A",
    STX: "X",
    STY: "Y"
  };
  if (mnemonic in storeMap) {
    const source = storeMap[mnemonic];
    return {
      keywords: ["store", `register ${source.toLowerCase()}`],
      summary: `Stores ${source} into memory.`
    };
  }

  const flagBranchMap: Record<string, string> = {
    BCC: "carry is clear",
    BCS: "carry is set",
    BEQ: "zero is set",
    BMI: "negative is set",
    BNE: "zero is clear",
    BPL: "negative is clear",
    BVC: "overflow is clear",
    BVS: "overflow is set"
  };
  if (mnemonic in flagBranchMap) {
    return {
      keywords: ["branch", "relative branch"],
      summary: `Branches to a relative target when ${flagBranchMap[mnemonic]}.`
    };
  }

  const flagJumpMap: Record<string, string> = {
    JCC: "carry is clear",
    JCS: "carry is set",
    JEQ: "zero is set",
    JMI: "negative is set",
    JNE: "zero is clear",
    JPL: "negative is clear",
    JVC: "overflow is clear",
    JVS: "overflow is set"
  };
  if (mnemonic in flagJumpMap) {
    return {
      keywords: ["jump", "conditional jump"],
      summary: `Jumps to an absolute target when ${flagJumpMap[mnemonic]}.`
    };
  }

  const transferMap: Record<string, string> = {
    TAX: "Transfers A into X and updates N/Z.",
    TAY: "Transfers A into Y and updates N/Z.",
    TSX: "Transfers SP into X and updates N/Z.",
    TSY: "Transfers SP into Y and updates N/Z.",
    TXA: "Transfers X into A and updates N/Z.",
    TXS: "Transfers X into SP without touching flags.",
    TYA: "Transfers Y into A and updates N/Z.",
    TYS: "Transfers Y into SP without touching flags."
  };
  if (mnemonic in transferMap) {
    return { keywords: ["transfer", "move register"], summary: transferMap[mnemonic] };
  }

  const flagControlMap: Record<string, string> = {
    CLC: "Clears carry.",
    CLI: "Clears interrupt disable so maskable IRQs may be taken again.",
    SEC: "Sets carry.",
    SEI: "Sets interrupt disable to mask normal IRQ handling."
  };
  if (mnemonic in flagControlMap) {
    return { keywords: ["flag control"], summary: flagControlMap[mnemonic] };
  }

  const arithmeticMap: Record<string, { keywords: string[]; summary: string }> = {
    ADC: {
      keywords: ["add", "carry"],
      summary: "Adds memory or an immediate operand to A, including the carry input."
    },
    SBC: {
      keywords: ["subtract", "borrow"],
      summary: "Subtracts memory or an immediate operand from A using carry as inverted borrow."
    },
    CMP: {
      keywords: ["compare", "flags only"],
      summary: "Compares A against memory or an immediate operand and updates flags without storing a result."
    }
  };
  if (mnemonic in arithmeticMap) {
    return arithmeticMap[mnemonic];
  }

  const logicMap: Record<string, { keywords: string[]; summary: string }> = {
    AND: { keywords: ["bitwise and", "logic"], summary: "Bitwise-ANDs A with memory or an immediate operand." },
    EOR: { keywords: ["bitwise xor", "logic"], summary: "Bitwise-exclusive-ORs A with memory or an immediate operand." },
    ORA: { keywords: ["bitwise or", "logic"], summary: "Bitwise-ORs A with memory or an immediate operand." }
  };
  if (mnemonic in logicMap) {
    return logicMap[mnemonic];
  }

  const shiftMap: Record<string, string> = {
    ASL: "Shifts the operand left by one bit and moves the outgoing top bit into carry.",
    ASR: "Performs an arithmetic right shift, preserving the sign bit while shifting right.",
    LSR: "Shifts the operand right by one bit and moves the outgoing low bit into carry.",
    ROL: "Rotates the operand left through carry.",
    ROR: "Rotates the operand right through carry."
  };
  if (mnemonic in shiftMap) {
    return { keywords: ["shift", "rotate"], summary: shiftMap[mnemonic] };
  }

  const stackMap: Record<string, string> = {
    PHA: "Pushes A onto the hardware stack.",
    PHP: "Pushes the status register onto the hardware stack.",
    PLA: "Pulls one byte from the hardware stack into A and updates N/Z.",
    PLP: "Pulls one byte from the hardware stack into the status register."
  };
  if (mnemonic in stackMap) {
    return { keywords: ["stack", "push/pop"], summary: stackMap[mnemonic] };
  }

  const incDecMap: Record<string, string> = {
    DEX: "Decrements X and updates N/Z.",
    DEY: "Decrements Y and updates N/Z.",
    INX: "Increments X and updates N/Z.",
    INY: "Increments Y and updates N/Z."
  };
  if (mnemonic in incDecMap) {
    return { keywords: ["increment", "decrement"], summary: incDecMap[mnemonic] };
  }

  switch (mnemonic) {
    case "BRK":
      return { keywords: ["software interrupt", "trap"], summary: "Triggers a software interrupt and vectors through the BRK/IRQ trap entry." };
    case "HLT":
      return { keywords: ["halt"], summary: "Halts CPU execution until the host or tooling resets or resumes the machine." };
    case "JMP":
      return { keywords: ["jump"], summary: "Replaces PC with an absolute or indirect target." };
    case "JSR":
      return { keywords: ["subroutine call", "stack"], summary: "Calls a subroutine by pushing the return address and jumping to an absolute target." };
    case "NOP":
      return { keywords: ["no operation"], summary: "Consumes its cycles without changing machine-visible state." };
    case "RTI":
      return { keywords: ["return from interrupt", "stack"], summary: "Restores status and PC from the stack after IRQ, NMI, or BRK handling." };
    case "RTS":
      return { keywords: ["return from subroutine", "stack"], summary: "Returns from a subroutine by pulling the saved PC and advancing past the call site." };
    default:
      return { keywords: [], summary: `${mnemonic} instruction family.` };
  }
}

function buildAddressingModeEntries(): AddressingModeEntry[] {
  return ADDRESSING_MODE_SEEDS.map((seed) => {
    const modeId = seed.id.replace("addressing-mode-", "").replace(/-/g, "_");
    const specMode = ISA_SPEC.modes[modeId];
    if (specMode === undefined) {
      throw new Error(`missing ISA mode '${modeId}' for reference-guide addressing mode '${seed.id}'`);
    }
    return {
      aliases: Array.from(seed.aliases),
      bytes: specMode.size,
      effectiveAddress: seed.effectiveAddress,
      id: seed.id,
      keywords: Array.from(seed.keywords),
      kind: "addressing-mode",
      notes: Array.from(seed.notes),
      relatedIds: [],
      summary: seed.description,
      syntax: seed.syntax,
      title: seed.title,
      usedByIds: [],
      valueKind: seed.valueKind
    };
  });
}

function buildAssemblerGuideEntries(): AssemblerGuideEntry[] {
  return ASSEMBLER_GUIDE_SEEDS.map((seed) => ({
    aliases: Array.from(seed.aliases),
    examples: Array.from(seed.examples),
    id: seed.id,
    keywords: Array.from(seed.keywords),
    kind: "assembler-guide",
    notes: Array.from(seed.notes),
    relatedIds: Array.from(seed.relatedIds),
    source: seed.source,
    summary: seed.summary,
    syntax: seed.syntax,
    title: seed.title
  }));
}

function buildBasicGuideEntries(): BasicGuideEntry[] {
  return BASIC_GUIDE_SEEDS.map((seed) => ({
    aliases: Array.from(seed.aliases),
    examples: Array.from(seed.examples),
    id: seed.id,
    keywords: Array.from(seed.keywords),
    kind: "basic-guide",
    notes: Array.from(seed.notes),
    relatedIds: Array.from(seed.relatedIds),
    source: seed.source,
    summary: seed.summary,
    syntax: seed.syntax,
    title: seed.title
  }));
}

function buildDebuggerGuideEntries(): DebuggerGuideEntry[] {
  return DEBUGGER_GUIDE_SEEDS.map((seed) => ({
    aliases: Array.from(seed.aliases),
    examples: Array.from(seed.examples),
    id: seed.id,
    keywords: Array.from(seed.keywords),
    kind: "debugger-guide",
    notes: Array.from(seed.notes),
    relatedIds: Array.from(seed.relatedIds),
    source: seed.source,
    summary: seed.summary,
    syntax: seed.syntax,
    title: seed.title
  }));
}

function buildTrackerGuideEntries(): TrackerGuideEntry[] {
  return TRACKER_GUIDE_SEEDS.map((seed) => ({
    aliases: Array.from(seed.aliases),
    examples: Array.from(seed.examples),
    id: seed.id,
    keywords: Array.from(seed.keywords),
    kind: "tracker-guide",
    notes: Array.from(seed.notes),
    relatedIds: Array.from(seed.relatedIds),
    source: seed.source,
    summary: seed.summary,
    syntax: seed.syntax,
    title: seed.title
  }));
}

function buildForthGuideEntries(): ForthGuideEntry[] {
  return FORTH_GUIDE_SEEDS.map((seed) => ({
    aliases: Array.from(seed.aliases),
    examples: Array.from(seed.examples),
    id: seed.id,
    keywords: Array.from(seed.keywords),
    kind: "forth-guide",
    notes: Array.from(seed.notes),
    relatedIds: Array.from(seed.relatedIds),
    source: seed.source,
    summary: seed.summary,
    syntax: seed.syntax,
    title: seed.title
  }));
}

function buildShellCommandEntries(): ShellCommandEntry[] {
  return SHELL_COMMAND_SEEDS.map((seed) => ({
    aliases: Array.from(seed.aliases),
    availability: seed.availability,
    examples: Array.from(seed.examples),
    id: seed.id,
    keywords: Array.from(seed.keywords),
    kind: "shell-command",
    notes: Array.from(seed.notes),
    relatedIds: Array.from(seed.relatedIds),
    summary: seed.summary,
    syntax: seed.syntax,
    title: seed.title
  }));
}

function buildFlagEntries(): FlagEntry[] {
  return FLAG_SEEDS.map((seed) => ({
    aliases: Array.from(seed.aliases),
    bit: seed.bit,
    clearedBy: Array.from(seed.clearedBy),
    id: seed.id,
    keywords: Array.from(seed.keywords),
    kind: "flag",
    meaning: seed.meaning,
    registerId: "register-p",
    relatedIds: Array.from(seed.relatedIds),
    setBy: Array.from(seed.setBy),
    summary: seed.summary,
    title: seed.title
  }));
}

function buildRegisterEntries(): RegisterEntry[] {
  return REGISTER_SEEDS.map((seed) => ({
    access: seed.access,
    address: seed.address,
    addressLabel: seed.addressLabel,
    aliases: Array.from(seed.aliases),
    bitFields: seed.bitFields.map((field) => ({ ...field })),
    id: seed.id,
    keywords: Array.from(seed.keywords),
    kind: "register",
    notes: Array.from(seed.notes),
    relatedIds: Array.from(seed.relatedIds),
    reset: seed.reset,
    scope: seed.scope,
    summary: seed.summary,
    title: seed.title,
    width: seed.width
  }));
}

function buildInterruptEntries(): InterruptEntry[] {
  return INTERRUPT_SEEDS.map((seed) => ({
    acknowledge: Array.from(seed.acknowledge),
    aliases: Array.from(seed.aliases),
    id: seed.id,
    keywords: Array.from(seed.keywords),
    kind: "interrupt",
    notes: Array.from(seed.notes),
    relatedIds: Array.from(seed.relatedIds),
    response: seed.response,
    sources: Array.from(seed.sources),
    summary: seed.summary,
    title: seed.title,
    vectors: seed.vectors.map((vector) => ({ ...vector }))
  }));
}

function buildMemoryRangeEntries(): MemoryRangeEntry[] {
  return MEMORY_RANGE_SEEDS.map((seed) => ({
    access: seed.access,
    aliases: Array.from(seed.aliases),
    end: seed.end,
    id: seed.id,
    keywords: Array.from(seed.keywords),
    kind: "memory-range",
    notes: Array.from(seed.notes),
    owner: seed.owner,
    relatedIds: Array.from(seed.relatedIds),
    start: seed.start,
    summary: seed.summary,
    title: seed.title
  }));
}

function buildPublicApiEntries(): PublicApiEntry[] {
  return PUBLIC_API_SEEDS.map((seed) => ({
    address: seed.address,
    addressLabel: seed.addressLabel,
    aliases: Array.from(seed.aliases),
    binding: seed.binding,
    id: seed.id,
    inputs: Array.from(seed.inputs),
    keywords: Array.from(seed.keywords),
    kind: "public-api",
    notes: Array.from(seed.notes),
    outputs: Array.from(seed.outputs),
    relatedIds: Array.from(seed.relatedIds),
    scope: seed.scope,
    summary: seed.summary,
    title: seed.title
  }));
}

function buildOpcodeEntries(addressingModes: AddressingModeEntry[]): OpcodeEntry[] {
  const modeLabelById = new Map(addressingModes.map((entry) => [entry.id.replace("addressing-mode-", "").replace(/-/g, "_"), entry.title]));
  const supplements = parseOpcodeSupplementByOpcode();
  const parsedRows = buildCanonicalOpcodeRows().map((row) => {
    const modeLabel = modeLabelById.get(row.modeId);
    if (modeLabel === undefined) {
      throw new Error(`unknown reference-guide mode '${row.modeId}' while parsing ${row.mnemonic} ${row.opcode}`);
    }
    const supplement = supplements.get(row.opcode);
    if (supplement === undefined) {
      throw new Error(`missing opcode supplement for ${row.opcode} (${row.mnemonic} ${row.modeId})`);
    }
    if (supplement.mnemonic !== row.mnemonic || supplement.modeId !== row.modeId) {
      throw new Error(
        `opcode supplement drift for ${row.opcode}: spec has ${row.mnemonic}/${row.modeId}, supplement has ${supplement.mnemonic}/${supplement.modeId}`
      );
    }
    return {
      mnemonic: row.mnemonic,
      variant: {
        bytes: row.bytes,
        cycles: supplement.cycles,
        flags: supplement.flags,
        flagsText: supplement.flagsText,
        modeId: row.modeId,
        modeLabel,
        opcode: row.opcode
      }
    };
  });
  const grouped = new Map<string, OpcodeVariant[]>();
  const seenOpcodes = new Set<string>();
  for (const { mnemonic, variant } of parsedRows) {
    if (seenOpcodes.has(variant.opcode)) {
      throw new Error(`duplicate opcode byte ${variant.opcode} in reference data`);
    }
    seenOpcodes.add(variant.opcode);
    const existing = grouped.get(mnemonic);
    if (existing === undefined) {
      grouped.set(mnemonic, [variant]);
    } else {
      existing.push(variant);
    }
  }
  for (const opcode of supplements.keys()) {
    if (!seenOpcodes.has(opcode)) {
      throw new Error(`opcode supplement ${opcode} is no longer present in spec/am8_isa.json`);
    }
  }

  const modeOrder = new Map(MODE_SAMPLE_ORDER.map((modeId, index) => [modeId, index]));
  return Array.from(grouped.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([mnemonic, opcodeVariants]) => {
      opcodeVariants.sort((left, right) => {
        const leftOrder = modeOrder.get(left.modeId) ?? Number.MAX_SAFE_INTEGER;
        const rightOrder = modeOrder.get(right.modeId) ?? Number.MAX_SAFE_INTEGER;
        return leftOrder - rightOrder || left.opcode.localeCompare(right.opcode);
      });
      const description = mnemonicDescription(mnemonic);
      const affectedFlags = uniqueStrings(opcodeVariants.flatMap((variant) => variant.flags));
      const relatedIds = uniqueStrings([
        ...opcodeVariants.map((variant) => modeIdToEntryId(variant.modeId)),
        ...affectedFlags.map((flag) => FLAG_ID_BY_NAME[flag] ?? ""),
        ...relatedRegistersForMnemonic(mnemonic)
      ]);
      return {
        affectedFlags,
        aliases: uniqueStrings([
          mnemonic.toLowerCase(),
          ...opcodeVariants.map((variant) => variant.opcode),
          ...opcodeVariants.map((variant) => variant.opcode.replace(/^0x/, "$")),
          ...opcodeVariants.map((variant) => variant.opcode.replace(/^0x/, ""))
        ]),
        examples: opcodeVariants.slice(0, 3).map((variant) => buildExample(mnemonic, variant.modeId)),
        id: `opcode-${mnemonic.toLowerCase()}`,
        keywords: uniqueStrings([
          ...description.keywords,
          ...affectedFlags,
          ...opcodeVariants.map((variant) => variant.modeLabel.toLowerCase()),
          ...opcodeVariants.map((variant) => variant.cycles.toLowerCase())
        ]),
        kind: "opcode",
        relatedIds,
        summary: description.summary,
        title: mnemonic,
        variants: opcodeVariants
      };
    });
}

function linkAddressingModes(entries: ReferenceEntry[]): void {
  const byId = new Map(entries.map((entry) => [entry.id, entry]));
  const opcodeEntries = entries.filter((entry): entry is OpcodeEntry => entry.kind === "opcode");
  const groupedByMode = new Map<string, string[]>();
  for (const opcode of opcodeEntries) {
    for (const variant of opcode.variants) {
      const modeEntryId = modeIdToEntryId(variant.modeId);
      const usedBy = groupedByMode.get(modeEntryId);
      if (usedBy === undefined) {
        groupedByMode.set(modeEntryId, [opcode.id]);
      } else {
        usedBy.push(opcode.id);
      }
    }
  }

  for (const entry of entries) {
    if (entry.kind !== "addressing-mode") {
      continue;
    }
    entry.usedByIds = uniqueStrings(groupedByMode.get(entry.id) ?? []);
    entry.relatedIds = uniqueStrings([...entry.relatedIds, ...entry.usedByIds.slice(0, 8)]).filter((id) => byId.has(id));
  }
}

function normalizeRelatedIds(entries: ReferenceEntry[]): ReferenceEntry[] {
  const knownIds = new Set(entries.map((entry) => entry.id));
  return entries.map((entry) => ({
    ...entry,
    relatedIds: uniqueStrings(entry.relatedIds).filter((id) => knownIds.has(id))
  })) as ReferenceEntry[];
}

function createCounts(entries: ReferenceEntry[]): Record<ReferenceKind, number> {
  const counts: Record<ReferenceKind, number> = {
    "addressing-mode": 0,
    "assembler-guide": 0,
    "basic-guide": 0,
    "debugger-guide": 0,
    flag: 0,
    "forth-guide": 0,
    "tracker-guide": 0,
    interrupt: 0,
    "memory-range": 0,
    opcode: 0,
    "public-api": 0,
    register: 0,
    "shell-command": 0
  };
  for (const entry of entries) {
    counts[entry.kind] += 1;
  }
  return counts;
}

export function createReferenceCatalog(): ReferenceCatalog {
  const addressingModes = buildAddressingModeEntries();
  const entries = normalizeRelatedIds([
    ...buildAssemblerGuideEntries(),
    ...buildBasicGuideEntries(),
    ...buildDebuggerGuideEntries(),
    ...buildTrackerGuideEntries(),
    ...buildForthGuideEntries(),
    ...buildShellCommandEntries(),
    ...buildOpcodeEntries(addressingModes),
    ...addressingModes,
    ...buildPublicApiEntries(),
    ...buildInterruptEntries(),
    ...buildRegisterEntries(),
    ...buildFlagEntries(),
    ...buildMemoryRangeEntries()
  ]);
  linkAddressingModes(entries);
  const finalEntries = normalizeRelatedIds(entries);
  return {
    byId: new Map(finalEntries.map((entry) => [entry.id, entry])),
    commonLookupIds: Array.from(COMMON_LOOKUP_IDS),
    counts: createCounts(finalEntries),
    entries: finalEntries,
    highlightedIds: Array.from(HIGHLIGHTED_IDS),
    quickEntries: QUICK_ENTRIES.map((entry) => ({ ...entry }))
  };
}

export function categoryLabel(kind: ReferenceKind): string {
  return CATEGORY_LABELS[kind];
}

export function categoryOrder(): ReferenceKind[] {
  return Array.from(CATEGORY_ORDER);
}

export function parseReferenceHash(hash: string): string | null {
  return hash.startsWith(REFERENCE_HASH_PREFIX) ? hash.slice(REFERENCE_HASH_PREFIX.length) || null : null;
}

export function referenceHashForEntryId(entryId: string | null): string {
  return entryId === null ? "" : `${REFERENCE_HASH_PREFIX}${entryId}`;
}
